import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { SunIcon, MoonIcon } from '@/components/ui/icons'
import { selectIsAuthenticated, selectAppUser, selectAuthUser } from '@/features/Authentication/authSlice'
import { selectRestaurant } from '@/App/restaurantSlice'
import { useFirebaseAuth } from '@/features/Authentication/hooks/useFirebaseAuth'

interface HeaderProps {
  logoUrl?: string
  restaurantName?: string
  tagline?: string
  darkMode: boolean
  onToggleDarkMode: () => void
}

export function Header({ 
  logoUrl, 
  restaurantName: restaurantNameProp, 
  tagline, 
  darkMode, 
  onToggleDarkMode 
}: HeaderProps) {
  const navigate = useNavigate()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const appUser = useSelector(selectAppUser)
  const authUser = useSelector(selectAuthUser)
  const restaurant = useSelector(selectRestaurant)
  const { signOut } = useFirebaseAuth()

  const restaurantName = restaurant?.name || restaurantNameProp || 'Restaurant'

  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const displayName = appUser?.firstName
    || authUser?.displayName?.split(' ')[0]
    || 'Profile'

  const initials = (
    (appUser?.firstName?.[0] || authUser?.displayName?.[0] || 'U')
  ).toUpperCase()

  useEffect(() => {
    if (!showMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])

  const handleSignOut = async () => {
    setShowMenu(false)
    await signOut()
  }

  return (
    <header className="relative bg-brand-primary text-white pt-12 pb-8 px-4">
      {/* Top bar: dark mode (left) + login/profile (right) */}
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
        <button
          onClick={onToggleDarkMode}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
        >
          {darkMode ? (
            <SunIcon className="size-5" />
          ) : (
            <MoonIcon className="size-5" />
          )}
        </button>

        {isAuthenticated ? (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm/5 font-medium"
              aria-haspopup="true"
              aria-expanded={showMenu}
            >
              <span className="size-6 flex items-center justify-center rounded-full bg-white text-brand-primary text-xs font-bold">
                {initials}
              </span>
              {displayName}
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-surface-primary rounded-xl shadow-lg border border-border-light overflow-hidden z-50 animate-fade-in-up">
                <div className="px-4 py-3 border-b border-border-light">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {appUser?.firstName} {appUser?.lastName}
                  </p>
                  <p className="text-xs text-text-secondary truncate">
                    {appUser?.email || authUser?.email || appUser?.mobileNumber || ''}
                  </p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-surface-tertiary transition-colors"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm/5 font-medium"
          >
            <UserIcon className="size-4" />
            Log in
          </button>
        )}
      </div>
      
      <div className="relative">
        {logoUrl ? (
          <img 
            src={logoUrl} 
            alt={restaurantName}
            className="h-12 mx-auto mb-2 object-contain"
          />
        ) : (
          <h1 className="font-brand text-3xl text-center mb-2">
            {restaurantName}
          </h1>
        )}
        {tagline && (
          <p className="text-center text-white/80 text-sm/5">
            {tagline}
          </p>
        )}
      </div>
    </header>
  )
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  )
}
